import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Trash2,
  Copy,
  ExternalLink,
  File,
  Loader2,
  Plus,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useStorageFiles } from '../hooks';
import { formatSize, formatDate, downloadTextFile } from '../utils';
import type { StorageFile, StorageApi } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';

interface GroupSectionProps {
  name: string;
  prefix: string;
  cdnBaseUrl?: string;
  onDelete: (key: string) => void;
  onDeleteGroup: (prefix: string) => void;
  onUpload: (prefix: string, files: FileList | null) => Promise<void>;
  api: StorageApi;
  queryKeyAll: readonly string[];
  queryKeyFiles: (prefix: string) => readonly string[];
  acceptedFileTypes?: string;
  disabled?: boolean;
}

/** Filter out .keep placeholder files from display */
function filterKeepFiles(files: StorageFile[]): StorageFile[] {
  return files.filter((f) => !f.key.endsWith('/.keep'));
}

function getFileName(key: string): string {
  const parts = key.split('/');
  return parts[parts.length - 1];
}

function getFolderName(prefix: string): string {
  const parts = prefix.replace(/\/$/, '').split('/');
  return parts[parts.length - 1];
}

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text);
}

function filesToCdnLines(files: StorageFile[], cdnBaseUrl?: string): string[] {
  return files.map((f) => (cdnBaseUrl ? `${cdnBaseUrl}/${f.key}` : f.key));
}

function FileTable({
  files,
  cdnBaseUrl,
  onDelete,
}: {
  files: StorageFile[];
  cdnBaseUrl?: string;
  onDelete: (key: string) => void;
}) {
  if (files.length === 0) return null;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[40%]">Name</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Last Modified</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {files.map((file) => {
          const cdnUrl = cdnBaseUrl ? `${cdnBaseUrl}/${file.key}` : null;
          return (
            <TableRow key={file.key}>
              <TableCell className="flex items-center gap-2">
                <File className="h-4 w-4 text-muted-foreground" />
                {getFileName(file.key)}
              </TableCell>
              <TableCell>{formatSize(file.size)}</TableCell>
              <TableCell>{formatDate(file.lastModified)}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  {cdnUrl && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title="Copy CDN URL"
                        onClick={() => copyToClipboard(cdnUrl)}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        title="Open in new tab"
                        onClick={() => window.open(cdnUrl, '_blank')}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    title="Delete"
                    onClick={() => onDelete(file.key)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** Sub-group: collapsible nested section (no further nesting) */
function SubGroupSection({
  prefix,
  cdnBaseUrl,
  onDelete,
  onUpload,
  api,
  queryKeyFiles,
  acceptedFileTypes,
}: {
  prefix: string;
  cdnBaseUrl?: string;
  onDelete: (key: string) => void;
  onUpload: (prefix: string, files: FileList | null) => Promise<void>;
  api: StorageApi;
  queryKeyFiles: (prefix: string) => readonly string[];
  acceptedFileTypes?: string;
}) {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data, isLoading } = useStorageFiles(api, queryKeyFiles(prefix), prefix, {
    enabled: open,
    flat: true,
  });
  const files = filterKeepFiles(data?.files ?? []);
  const name = getFolderName(prefix);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border rounded-md ml-4">
      <div className="flex items-center justify-between px-3 py-2 bg-muted/20">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 font-medium text-sm">
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            {name}
            {open && !isLoading && (
              <span className="text-muted-foreground font-normal text-xs">
                ({files.length} file{files.length !== 1 ? 's' : ''})
              </span>
            )}
          </Button>
        </CollapsibleTrigger>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              downloadTextFile(`${name}-files.txt`, filesToCdnLines(files, cdnBaseUrl))
            }
            disabled={files.length === 0}
          >
            <Download className="h-3 w-3 mr-1" />
            Export Paths
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={acceptedFileTypes}
            className="hidden"
            onChange={(e) => {
              onUpload(prefix, e.target.files);
              if (e.target) e.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="h-3 w-3 mr-1" />
            Upload
          </Button>
        </div>
      </div>
      <CollapsibleContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading...
          </div>
        ) : files.length === 0 ? (
          <div className="flex items-center justify-center py-4 text-muted-foreground text-sm">
            <File className="h-4 w-4 mr-2" />
            No files
          </div>
        ) : (
          <FileTable files={files} cdnBaseUrl={cdnBaseUrl} onDelete={onDelete} />
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

export function GroupSection({
  name,
  prefix,
  cdnBaseUrl,
  onDelete,
  onDeleteGroup,
  onUpload,
  api,
  queryKeyAll,
  queryKeyFiles,
  acceptedFileTypes,
  disabled,
}: GroupSectionProps) {
  const [open, setOpen] = useState(true);
  const [showNewSubGroup, setShowNewSubGroup] = useState(false);
  const [newSubGroupName, setNewSubGroupName] = useState('');
  const [exporting, setExporting] = useState(false);
  const queryClient = useQueryClient();

  // Fetch with delimiter to get direct files + subfolder prefixes
  const { data, isLoading } = useStorageFiles(api, queryKeyFiles(prefix), prefix, {
    enabled: open,
    flat: false,
  });
  const files = filterKeepFiles(data?.files ?? []);
  const subFolders = data?.folders ?? [];

  const invalidateAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [...queryKeyAll] });
  }, [queryClient, queryKeyAll]);

  const handleCreateSubGroup = useCallback(async () => {
    const subName = newSubGroupName.trim();
    if (!subName || !/^[a-zA-Z0-9_-]+$/.test(subName)) return;

    try {
      const { uploadUrl } = await api.getUploadUrl(
        `${prefix}${subName}/.keep`,
        'application/octet-stream',
        1,
      );
      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/octet-stream' },
        body: new Uint8Array(1),
      });
      invalidateAll();
    } catch {
      // Ignore -- sub group will appear once a file is uploaded
    }

    setNewSubGroupName('');
    setShowNewSubGroup(false);
  }, [newSubGroupName, prefix, invalidateAll, api]);

  // Export all files recursively (direct + sub-group files)
  const handleExportAll = useCallback(async () => {
    setExporting(true);
    try {
      const result = await api.listFiles(prefix, '', 1000);
      const allFiles = filterKeepFiles(result.files);
      const cdn = cdnBaseUrl || result.cdnBaseUrl || undefined;
      downloadTextFile(`${name}-files.txt`, filesToCdnLines(allFiles, cdn));
    } finally {
      setExporting(false);
    }
  }, [prefix, name, cdnBaseUrl, api]);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border rounded-lg">
      <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 font-semibold text-base">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {name}
            {!isLoading && (
              <span className="text-muted-foreground font-normal text-sm">
                ({files.length} file{files.length !== 1 ? 's' : ''}
                {subFolders.length > 0 &&
                  `, ${subFolders.length} sub-group${subFolders.length !== 1 ? 's' : ''}`}
                )
              </span>
            )}
          </Button>
        </CollapsibleTrigger>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={handleExportAll} disabled={exporting}>
            <Download className="h-3.5 w-3.5 mr-1" />
            {exporting ? 'Exporting...' : 'Export All Paths'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            disabled={disabled}
            onClick={() => onDeleteGroup(prefix)}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            Delete Group
          </Button>
        </div>
      </div>
      <CollapsibleContent className="space-y-2 pb-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Loading files...
          </div>
        ) : (
          <>
            {/* Direct files in this group */}
            {files.length === 0 && subFolders.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <File className="h-5 w-5 mr-2" />
                No files in this group
              </div>
            ) : (
              <FileTable files={files} cdnBaseUrl={cdnBaseUrl} onDelete={onDelete} />
            )}

            {/* Sub-groups */}
            {subFolders.map((subPrefix) => (
              <SubGroupSection
                key={subPrefix}
                prefix={subPrefix}
                cdnBaseUrl={cdnBaseUrl}
                onDelete={onDelete}
                onUpload={onUpload}
                api={api}
                queryKeyFiles={queryKeyFiles}
                acceptedFileTypes={acceptedFileTypes}
              />
            ))}

            {/* New sub-group form */}
            {showNewSubGroup ? (
              <div className="flex items-center gap-2 ml-4 p-2 border rounded-md bg-muted/10">
                <Input
                  placeholder="Sub-group name"
                  value={newSubGroupName}
                  onChange={(e) => setNewSubGroupName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateSubGroup()}
                  className="max-w-xs h-8 text-sm"
                />
                <Button
                  size="sm"
                  className="h-8"
                  disabled={
                    !newSubGroupName.trim() || !/^[a-zA-Z0-9_-]+$/.test(newSubGroupName.trim())
                  }
                  onClick={handleCreateSubGroup}
                >
                  Create
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => setShowNewSubGroup(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="ml-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={() => setShowNewSubGroup(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  New Sub Group
                </Button>
              </div>
            )}
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
